<template>
  <div
    ref="turnstile"
    class="turnstile"
    :class="{ 'turnstile--visible': isVisible }"
  />
</template>

<script>
const TURNSTILE_LOAD_FUNCTION_NAME = "cfTurnstileOnLoad";
const TURNSTILE_SCRIPT_SRC = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${TURNSTILE_LOAD_FUNCTION_NAME}&render=explicit`;
const DEFAULT_EXPIRE_TIMEOUT = 299999;
const DEFAULT_RETRY_INTERVAL = 120000;

export default {
  name: "TurnstileWidget",

  props: {
    /**
     * The site key.
     */
    sitekey: {
      type: String,
      required: true,
    },

    /**
     * Indicates whether the widget should automatically retry or not.
     *
     * @remarks The available options are 'auto' and 'never'.
     */
    retry: {
      type: String,
      default: "auto",
    },

    /**
     * The expire timeout, by default turnstile set it in 300 seconds.
     */
    expireTimeout: {
      type: Number,
      default: DEFAULT_EXPIRE_TIMEOUT,
    },

    /**
     * The two way binding model value that represents the token.
     */
    value: {
      type: String,
      default: undefined,
    },
  },

  emits: ["input", "error", "ready", "verify", "expired", "fatal-error"],

  data() {
    return {
      widgetId: undefined,
      scriptElement: undefined,
      expireTimer: undefined,
      isVisible: false,
    };
  },

  mounted() {
    this.setupScript();
  },

  beforeDestroy() {
    if (_.isElement(this.scriptElement)) {
      this.scriptElement.removeEventListener("error", this.onScriptLoadError);
    }

    clearTimeout(this.expireTimer);
    this.remove();
  },

  methods: {
    /**
     * Executes the widget.
     */
    execute() {
      const execute = this.wrapWithErrorHandling(window.turnstile.execute);

      if (_.isFunction(execute)) {
        execute(this.$refs.turnstile);
      }
    },

    /**
     * Renders the widget.
     */
    render() {
      const render = this.wrapWithErrorHandling(window.turnstile.render);

      if (_.isFunction(render)) {
        const options = {
          sitekey: this.sitekey,
          callback: this.onChallengeSuccess,
          "expired-callback": this.onExpired,
          "error-callback": this.onChallengeFail,
          "before-interactive-callback": this.onBeforeInteractiveChallenge,
          "retry-interval": DEFAULT_RETRY_INTERVAL,
          retry: this.retry,
        };

        this.widgetId = render(this.$refs.turnstile, options);
      }
    },

    /**
     * Wraps a callback with fatal-error handling mechanism.
     */
    wrapWithErrorHandling(callback) {
      return (...args) => {
        try {
          callback(...args);
        } catch {
          this.$emit("fatal-error");
        }
      };
    },

    /**
     * Resets the widget.
     */
    reset() {
      const reset = window.turnstile.reset;

      if (_.isFunction(reset)) {
        this.$emit("input", null);

        reset(this.widgetId);
      }
    },

    /**
     * Removes the widget.
     */
    remove() {
      const remove = window.turnstile.remove;

      if (_.isFunction(remove)) {
        remove(this.widgetId);
        this.widgetId = undefined;
      }
    },

    /**
     * Starts the expire timeout timer.
     */
    startExpireTimeout() {
      this.expireTimer = setTimeout(() => {
        this.onExpired();
      }, this.expireTimeout);
    },

    /**
     * Setups the turnstile script.
     */
    setupScript() {
      const isWidgetLoaded = !_.isNil(window.turnstile);

      window[TURNSTILE_LOAD_FUNCTION_NAME] = this.onScriptLoad;

      if (isWidgetLoaded) {
        this.render();
      } else {
        const script = document.createElement("script");

        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.addEventListener("error", this.onScriptLoadError);

        document.head.appendChild(script);

        this.scriptElement = script;
      }
    },

    /**
     * Occurs when the script loaded results in error.
     */
    onScriptLoadError() {
      this.$emit("error");
    },

    /**
     * Occurs when the turnstile script is loaded.
     */
    onScriptLoad() {
      this.$emit("ready");

      this.render();
    },

    /**
     * Occurs when the turnstile challenge is successfully completed.
     */
    onChallengeSuccess(token) {
      this.$emit("input", token);

      this.$nextTick(() => {
        this.$emit("verify", token);

        this.startExpireTimeout();
      });
    },

    /**
     * Occurs when the challenges has failed.
     */
    onChallengeFail() {
      this.$emit("error");
    },

    /**
     * Occurs before enters interactive challenge.
     */
    onBeforeInteractiveChallenge() {
      this.isVisible = true;
    },

    /**
     * Occurs when the turnstile has expired.
     */
    onExpired() {
      this.$emit("expired");

      this.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.turnstile {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
}
</style>
